import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import styled, { css } from 'styled-components';
import { changeColorAlpha } from '@elfsight/lib-utils';
import { getBrightInvertedColor } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { CarouselProvider } from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';
import { meta } from '../../../../meta';
import { useResizeWidth } from '../../../utils';
import { calcSliderIndexesOfImagesToLoad } from '../../lazy-loading';
import { ThumbnailsMainSlider } from './ThumbnailsMainSlider';
import { ThumbnailsSecondSlider } from './ThumbnailsSecondSlider';

export const Thumbnails = ({ images, reportVisibleImages }) => {
  const containerRef = useRef(null);
  const [columnsCount, setColumnsCount] = useState(0);
  const [rowsCount, setRowsCount] = useState(0);
  const [mainIsDrag, setMainIsDrag] = useState(false);
  const [currentItemId, setCurrentItemId] = useState(0);
  const [secondSliderSlideCount, setSecondSliderSlideCount] = useState(0);
  const [secondSliderCurrentSlide, setSecondSliderCurrentSlide] =
    useState(currentItemId);
  const {
    thumbnailsImageRatio,
    thumbnailsImageResize,
    thumbnailsThumbnailsPlacement,
    thumbnailsThumbnailsSize,
    thumbnailsThumbnailsGutter,
    thumbnailsThumbnailsArrowsColor,
    backgroundColor,
    imageCopyProtection,
    sliderLoop,
    sliderAnimationType,
    sliderTransitionDuration,
    backgroundType
  } = useSettings();
  const { measureRef, width } = useResizeWidth();

  const [rerenderCarousel, setRerenderCarousel] = useState(true);
  const [secondSliderCurrentVisibleSlide, setSecondSliderCurrentVisibleSlide] =
    useState(secondSliderCurrentSlide);
  const totalSlides = images.length;

  useEffect(() => {
    const visibleSlidesCount = Math.max(secondSliderSlideCount, 1);

    const indexesOfImagesToLoad = calcSliderIndexesOfImagesToLoad({
      currentIndex: secondSliderCurrentVisibleSlide,
      visibleSlidesCount: visibleSlidesCount,
      maxIndex: images.length - 1,
      step: visibleSlidesCount
    });

    if (
      currentItemId < secondSliderCurrentVisibleSlide ||
      currentItemId > secondSliderCurrentVisibleSlide + secondSliderSlideCount
    ) {
      indexesOfImagesToLoad.push(currentItemId);
    }

    reportVisibleImages(indexesOfImagesToLoad.map((index) => images[index].id));
  }, [
    currentItemId,
    secondSliderSlideCount,
    secondSliderCurrentSlide,
    secondSliderCurrentVisibleSlide,
    images,
    reportVisibleImages
  ]);

  useEffect(() => {
    setRerenderCarousel(false);
    setTimeout(() => {
      setRerenderCarousel(true);
    });
  }, [sliderLoop]);

  function checkCopyProtection(e) {
    if (imageCopyProtection) e.preventDefault();
  }

  const updateLayout = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    const { clientWidth, clientHeight } = containerRef.current;
    const nextColumnsCount =
      Math.floor(clientWidth / thumbnailsThumbnailsSize) || 1;
    const nextRowsCount =
      Math.floor(
        clientHeight / (thumbnailsThumbnailsSize * thumbnailsImageRatio)
      ) || 1;

    setColumnsCount(nextColumnsCount);
    setRowsCount(nextRowsCount);
  }, [thumbnailsImageRatio, thumbnailsThumbnailsSize]);

  useLayoutEffect(() => {
    const resizeObserver = new ResizeObserver(() =>
      requestAnimationFrame(updateLayout)
    );
    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, [updateLayout]);

  useEffect(() => {
    const slideCount = ['left', 'right'].includes(thumbnailsThumbnailsPlacement)
      ? rowsCount
      : columnsCount;
    setSecondSliderSlideCount(slideCount);
  }, [thumbnailsThumbnailsPlacement, rowsCount, columnsCount]);

  const secondSliderPage = useRef(1);

  useEffect(() => {
    if (!secondSliderSlideCount) {
      return;
    }

    const nextPage = Math.ceil((currentItemId + 1) / secondSliderSlideCount);

    if (secondSliderPage.current !== nextPage) {
      secondSliderPage.current = nextPage;
      setSecondSliderCurrentSlide(
        secondSliderPage.current * secondSliderSlideCount -
          secondSliderSlideCount
      );
    }
  }, [currentItemId, secondSliderSlideCount]);

  return (
    <Container
      ref={containerRef}
      $placement={thumbnailsThumbnailsPlacement}
      $ratio={thumbnailsImageRatio}
      $resize={thumbnailsImageResize}
      $gutter={thumbnailsThumbnailsGutter}
    >
      <MainWrapper
        ref={measureRef}
        $arrowsColor={thumbnailsThumbnailsArrowsColor}
      >
        {rerenderCarousel && (
          <CarouselProvider
            naturalSlideWidth={100}
            naturalSlideHeight={100 * thumbnailsImageRatio}
            totalSlides={totalSlides}
            visibleSlides={1}
            currentSlide={currentItemId}
            infinite={sliderLoop}
          >
            <ThumbnailsMainSlider
              width={width}
              images={images}
              totalSlides={totalSlides}
              currentItemId={currentItemId}
              setCurrentItemId={setCurrentItemId}
              sliderAnimationType={sliderAnimationType}
              mainIsDrag={mainIsDrag}
              setMainIsDrag={setMainIsDrag}
              sliderTransitionDuration={sliderTransitionDuration}
              thumbnailsThumbnailsArrowsColor={thumbnailsThumbnailsArrowsColor}
            />
          </CarouselProvider>
        )}
      </MainWrapper>

      <SecondWrapper
        $placement={thumbnailsThumbnailsPlacement}
        $size={thumbnailsThumbnailsSize}
        $gutter={thumbnailsThumbnailsGutter}
        $bgColor={backgroundColor}
        $bgType={backgroundType}
      >
        {rerenderCarousel && (
          <CarouselProvider
            naturalSlideWidth={100}
            naturalSlideHeight={100 * thumbnailsImageRatio}
            totalSlides={totalSlides}
            currentSlide={secondSliderCurrentSlide}
            visibleSlides={secondSliderSlideCount}
            orientation={
              ['bottom', 'top'].includes(thumbnailsThumbnailsPlacement)
                ? 'horizontal'
                : 'vertical'
            }
            lockOnWindowScroll
            infinite={sliderLoop}
          >
            <ThumbnailsSecondSlider
              images={images}
              totalSlides={totalSlides}
              currentItemId={currentItemId}
              setCurrentItemId={setCurrentItemId}
              setCurrentVisibleItemId={setSecondSliderCurrentVisibleSlide}
              sliderAnimationType={sliderAnimationType}
              mainIsDrag={mainIsDrag}
              setMainIsDrag={setMainIsDrag}
              checkCopyProtection={checkCopyProtection}
            />
          </CarouselProvider>
        )}
      </SecondWrapper>
    </Container>
  );
};

const Container = styled.div.attrs({
  className: `${meta.stylePrefix}-thumbnails-component`
})`
  width: 100%;
  display: flex;

  ${({ $placement }) => {
    if (['bottom', 'top'].includes($placement)) {
      return css`
        flex-direction: ${$placement === 'bottom'
          ? 'column'
          : 'column-reverse'};
      `;
    }

    return css`
      flex-direction: ${$placement === 'right' ? 'row' : 'row-reverse'};
    `;
  }}

  & .${meta.stylePrefix}-thumbnails-imageContainer {
    padding-top: ${({ $ratio }) => $ratio * 100}%;
  }

  &
    .${meta.stylePrefix}-thumbnails-image,
    &
    .${meta.stylePrefix}-thumbnails-thumbnailImage {
    object-fit: ${({ $resize }) => ($resize === 'crop' ? 'cover' : 'contain')};
  }

  & .${meta.stylePrefix}-thumbnails-thumbnailImage {
    padding: ${({ $gutter }) => $gutter / 2}px;
  }

  & .${meta.stylePrefix}-thumbnails-thumbnailImageContainer::after {
    top: ${({ $gutter }) => $gutter / 2}px;
    left: ${({ $gutter }) => $gutter / 2}px;
    right: ${({ $gutter }) => $gutter / 2}px;
    bottom: ${({ $gutter }) => $gutter / 2}px;
  }

  &
    .${meta.stylePrefix}-item-data-overlay,
    &
    .${meta.stylePrefix}-item-data-imageBackground {
    width: ${({ $resize }) => ($resize === 'crop' ? '100%' : '')};
    height: ${({ $resize }) => ($resize === 'crop' ? '100%' : '')};
  }
`;

const MainWrapper = styled.div.attrs({
  className: `${meta.stylePrefix}-thumbnails-main`
})`
  width: 100%;
  position: relative;

  & .${meta.stylePrefix}-arrow-icon {
    fill: ${({ $arrowsColor }) =>
      getBrightInvertedColor($arrowsColor, [
        'rgb(255, 255, 255)',
        'rgb(29, 33, 41)'
      ])};
  }
`;

const SecondWrapper = styled.div.attrs({
  className: `${meta.stylePrefix}-thumbnails-second`
})`
  width: 100%;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;

  margin: -${({ $gutter }) => $gutter / 2}px;

  ${({ $placement, $size }) =>
    ['left', 'right'].includes($placement) &&
    css`
      width: ${$size}px;
    `}

  ${({ $placement }) => $placement === 'bottom' && `margin-top: 40px;`}
  ${({ $placement }) => $placement === 'top' && `margin-bottom: 40px;`}
  ${({ $placement }) => $placement === 'left' && `margin-right: 40px;`}
  ${({ $placement }) => $placement === 'right' && `margin-left: 40px;`}

  & .${meta.stylePrefix}-thumbnails-secondButtonBack, & .${meta.stylePrefix}-thumbnails-secondButtonNext {
    ${({ $placement }) =>
      ['left', 'right'].includes($placement) &&
      css`
        left: 0;
        right: 0;
        height: 24px;
        width: 100%;
        transform: none;
      `}
  }

  & .${meta.stylePrefix}-thumbnails-secondButtonBack {
    ${({ $placement }) =>
      ['left', 'right'].includes($placement) &&
      css`
        top: 0;
        bottom: auto;
      `}

    &::after {
      ${({ $bgType, $bgColor }) =>
        $bgType === 'color' &&
        css`
          background-image: linear-gradient(
            to left,
            ${changeColorAlpha($bgColor, -1)},
            ${$bgColor} 50%
          );
        `}
    }

    & .${meta.stylePrefix}-arrow-icon {
      ${({ $placement }) =>
        ['left', 'right'].includes($placement) &&
        css`
          transform: rotateZ(-90deg);
        `}
    }
  }

  & .${meta.stylePrefix}-thumbnails-secondButtonNext {
    ${({ $placement }) =>
      ['left', 'right'].includes($placement) &&
      css`
        top: auto;
        bottom: 0;
      `}

    &::after {
      ${({ $bgType, $bgColor }) =>
        $bgType === 'color' &&
        css`
          background-image: linear-gradient(
            to right,
            ${changeColorAlpha($bgColor, -1)},
            ${$bgColor} 50%
          );
        `}
    }

    & .${meta.stylePrefix}-arrow-icon {
      ${({ $placement }) =>
        ['left', 'right'].includes($placement) &&
        css`
          transform: rotateZ(90deg) !important;
        `}
    }
  }

  & .${meta.stylePrefix}-arrow-icon {
    fill: ${({ $bgColor }) =>
      getBrightInvertedColor($bgColor, [
        'rgb(255, 255, 255)',
        'rgb(29, 33, 41)'
      ])};
  }

  & .${meta.stylePrefix}-thumbnails-thumbnailActive::after {
    border-color: ${({ $bgColor }) =>
      getBrightInvertedColor($bgColor, [
        'rgb(255, 255, 255)',
        'rgb(29, 33, 41)'
      ])};
  }

  & .${meta.stylePrefix}-thumbnails-thumbnailImageContainer:hover::after {
    border-color: ${({ $bgColor }) =>
      getBrightInvertedColor($bgColor, [
        'rgb(255, 255, 255)',
        'rgb(29, 33, 41)'
      ])};
  }
`;
